// Subagent blueprints: specialists the main agents can hand work to with
// `sessions_spawn({agentId})` — who it speaks as (if anyone) and the
// profile it learns from its libraries, its own skills, knowledge and
// memory, no channel and no desktop (docs/subagent-blueprints-contract.md
// §5). Every write goes through routes/subagent_blueprints.py, which
// validates the references and the spawn graph BEFORE it writes, so a
// refused save leaves nothing behind; its `{detail: {code, message}}` is
// what `blueprintError` shows.
//
// Every view the hooks return drops the graph's `unreachable` issue ("no
// agent may spawn it yet"): nothing calling a subagent is now a choice the
// operator makes ("Only workflows and jobs", WhoCanCall), not a fault, and
// 16 of 18 subagents in a real workspace are run only by workflows and
// jobs. Cards, the header, the Spawning section and the org chart all read
// through these hooks, so one filter fixes them all. Real errors and
// limit_above_cap stay.

import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";

import { ApiError, apiFetch } from "@/lib/api";
import type { components } from "@/types/api.gen";

export type SubagentBlueprint = components["schemas"]["SubagentBlueprint"];
export type SubagentBlueprintView = components["schemas"]["SubagentBlueprintView"];
export type SubagentBlueprintsResponse = components["schemas"]["SubagentBlueprintsResponse"];
export type SubagentCaps = components["schemas"]["SubagentCaps"];
export type SubagentCapabilities = components["schemas"]["SubagentCapabilities"];
export type SubagentLimits = components["schemas"]["SubagentLimits"];
export type EffectiveAccess = components["schemas"]["EffectiveAccess"];
export type SpawnGraphView = components["schemas"]["GraphView"];
export type GraphIssue = components["schemas"]["GraphIssueView"];
export type CreateSubagentBlueprintBody = components["schemas"]["CreateSubagentBlueprintBody"];
export type PatchSubagentBlueprintBody = components["schemas"]["PatchSubagentBlueprintBody"];
export type SubagentMemory = components["schemas"]["SubagentMemoryView"];
export type SubagentMemorySection = components["schemas"]["SubagentMemorySection"];
export type SubagentMemoryCurate = components["schemas"]["SubagentMemoryCurateResponse"];
export type SubagentMemoryCurateRunning = components["schemas"]["SubagentMemoryCurateRunning"];
export type SubagentMemoryCurateReply = SubagentMemoryCurate | SubagentMemoryCurateRunning;
export type SubagentMemoryDiffLine = components["schemas"]["SubagentMemoryDiffLine"];
export type MoveSubagentMemoryNoteBody = components["schemas"]["MoveSubagentMemoryNoteBody"];
export type SubagentProfileView = components["schemas"]["SubagentProfileView"];

/** The issue code that is a choice, not a fault (see the header). */
export const NOT_AN_ISSUE = "unreachable";

const keep = (i: { code: string }) => i.code !== NOT_AN_ISSUE;

/** A view without the `unreachable` issue. Pure (blueprints.selftest.ts). */
export function withoutChoiceIssues(view: SubagentBlueprintView): SubagentBlueprintView {
  const issues = view.issues;
  return issues && issues.some((i) => !keep(i)) ? { ...view, issues: issues.filter(keep) } : view;
}

/** The list read without the `unreachable` issue, on each subagent and in the graph. */
export function listWithoutChoiceIssues(data: SubagentBlueprintsResponse): SubagentBlueprintsResponse {
  return {
    ...data,
    blueprints: data.blueprints.map(withoutChoiceIssues),
    graph: { ...data.graph, errors: data.graph.errors.filter(keep), warnings: data.graph.warnings.filter(keep) },
  };
}

const base = (customerId: string) => `/api/customers/${customerId}/subagent-blueprints`;
const one = (customerId: string, template: string) => `${base(customerId)}/${encodeURIComponent(template)}`;

export const blueprintKeys = {
  list: (customerId: string | null) => ["subagent-blueprints", customerId] as const,
  one: (customerId: string | null, template: string | null) => ["subagent-blueprint", customerId, template] as const,
  memory: (customerId: string | null, template: string | null) => ["subagent-memory", customerId, template] as const,
  profile: (customerId: string | null, template: string | null) => ["subagent-profile", customerId, template] as const,
};

/** Blueprints, the customer-wide caps and the spawn graph, from one read. */
export function useSubagentBlueprints(customerId: string | null) {
  return useQuery({
    queryKey: blueprintKeys.list(customerId),
    enabled: !!customerId,
    queryFn: ({ signal }) => apiFetch<SubagentBlueprintsResponse>(base(customerId!), { signal }),
    select: listWithoutChoiceIssues,
    staleTime: 5_000,
  });
}

export function useSubagentBlueprint(customerId: string | null, template: string | null) {
  return useQuery({
    queryKey: blueprintKeys.one(customerId, template),
    enabled: !!customerId && !!template,
    queryFn: ({ signal }) => apiFetch<SubagentBlueprintView>(one(customerId!, template!), { signal }),
    select: withoutChoiceIssues,
    staleTime: 5_000,
    // A missing blueprint is an answer, not a blip to retry.
    retry: (count, error) => !(error instanceof ApiError && error.status === 404) && count < 1,
  });
}

/**
 * One subagent as saved NOW (never the cache): what a save that sends the
 * whole block builds on, so a change made elsewhere since the page loaded
 * is kept. The answer goes into the cache too.
 */
export function fetchSubagentBlueprint(qc: QueryClient, customerId: string, template: string): Promise<SubagentBlueprintView> {
  return qc.fetchQuery({
    queryKey: blueprintKeys.one(customerId, template),
    queryFn: ({ signal }) => apiFetch<SubagentBlueprintView>(one(customerId, template), { signal }),
    staleTime: 0,
    retry: false,
  });
}

/** A blueprint change moves the roster (directory, org chart), every
 *  other blueprint's graph verdict and the Subagents page's groups (a
 *  delete leaves its group; the starting groups follow the manifest), so
 *  all of those refetch. */
function invalidateAll(qc: QueryClient, customerId: string) {
  void qc.invalidateQueries({ queryKey: ["subagent-blueprints", customerId] });
  void qc.invalidateQueries({ queryKey: ["subagent-blueprint", customerId] });
  void qc.invalidateQueries({ queryKey: ["configured-agents"] });
  void qc.invalidateQueries({ queryKey: ["customer-agents"] });
  void qc.invalidateQueries({ queryKey: ["org-chart", customerId] });
  void qc.invalidateQueries({ queryKey: ["knowledge", customerId] });
  void qc.invalidateQueries({ queryKey: ["subagent-groups", customerId] });
}

function onSaved(qc: QueryClient, customerId: string, view: SubagentBlueprintView) {
  qc.setQueryData(blueprintKeys.one(customerId, view.template), view);
  invalidateAll(qc, customerId);
}

export function useCreateSubagentBlueprint() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ customerId, body }: { customerId: string; body: CreateSubagentBlueprintBody }) =>
      apiFetch<SubagentBlueprintView>(base(customerId), { method: "POST", body }),
    onSuccess: (view, vars) => onSaved(qc, vars.customerId, view),
  });
}

/** `subagent` is a FULL block replace (the server never merges), so a
 *  caller sends the saved block with its own fields changed.
 *  `errorHandled`: the caller shows every error itself (no app toast). */
export function usePatchSubagentBlueprint(errorHandled = false) {
  const qc = useQueryClient();
  return useMutation({
    meta: errorHandled ? { errorHandled: true } : undefined,
    mutationFn: ({ customerId, template, patch }: { customerId: string; template: string; patch: PatchSubagentBlueprintBody }) =>
      apiFetch<SubagentBlueprintView>(one(customerId, template), { method: "PATCH", body: patch }),
    onSuccess: (view, vars) => onSaved(qc, vars.customerId, view),
  });
}

export function useDeleteSubagentBlueprint(errorHandled = false) {
  const qc = useQueryClient();
  return useMutation({
    meta: errorHandled ? { errorHandled: true } : undefined,
    mutationFn: ({ customerId, template }: { customerId: string; template: string }) =>
      apiFetch<{ ok: boolean; template: string }>(one(customerId, template), { method: "DELETE" }),
    onSuccess: (_d, vars) => {
      qc.removeQueries({ queryKey: blueprintKeys.one(vars.customerId, vars.template) });
      invalidateAll(qc, vars.customerId);
    },
  });
}

/**
 * The profile Corvus builds from the libraries it learns from (who it
 * speaks as, or what a folder teaches), with its text: GET …/{t}/profile
 * (services/subagents/profile.py). Read only when the operator opens it
 * ("Read"); polls while a build is running. The view's own `profile` (no
 * text) is what the row shows otherwise.
 */
export function useSubagentProfile(customerId: string | null, template: string | null, enabled = true) {
  return useQuery({
    queryKey: blueprintKeys.profile(customerId, template),
    enabled: enabled && !!customerId && !!template,
    queryFn: ({ signal }) => apiFetch<SubagentProfileView>(`${one(customerId!, template!)}/profile`, { signal }),
    staleTime: 5_000,
    retry: (count, error) => !(error instanceof ApiError && error.status === 404) && count < 1,
    refetchInterval: (q) => (q.state.data?.state === "building" || q.state.data?.state === "waiting" ? 3000 : false),
  });
}

/** Build the profile again from everything in its libraries now (202; the view says `building`). */
export function useRebuildSubagentProfile() {
  const qc = useQueryClient();
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: ({ customerId, template }: { customerId: string; template: string }) =>
      apiFetch<{ state: string }>(`${one(customerId, template)}/profile/rebuild`, { method: "POST" }),
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: blueprintKeys.profile(vars.customerId, vars.template) });
      void qc.invalidateQueries({ queryKey: blueprintKeys.one(vars.customerId, vars.template) });
    },
  });
}

/**
 * Its workspace MEMORY.md (services/subagent_memory.py): what its runs and
 * the coaching chat saved, parsed into sections. Not the team agents'
 * memories table. The coaching chat refetches it after every turn
 * (`refreshSubagentMemory`), since a coaching save happens in that turn.
 */
export function useSubagentMemory(customerId: string | null, template: string | null) {
  return useQuery({
    queryKey: blueprintKeys.memory(customerId, template),
    enabled: !!customerId && !!template,
    queryFn: ({ signal }) => apiFetch<SubagentMemory>(`${one(customerId!, template!)}/memory`, { signal }),
    staleTime: 5_000,
    retry: (count, error) => !(error instanceof ApiError && error.status === 404) && count < 1,
  });
}

export function refreshSubagentMemory(qc: QueryClient, customerId: string, template: string) {
  void qc.invalidateQueries({ queryKey: blueprintKeys.memory(customerId, template) });
}

/** Replaces the whole file (the operator's edit); runs read it at their start. */
/**
 * The whole file replaced. `since` = the file time the text was based on
 * (the view's `updated_at`): a file that changed meanwhile is refused (409
 * `changed_meanwhile`) and reloaded here; omit it to replace whatever is
 * there. `guard_runs` refuses (409 `busy` / `runs_unknown`) while the
 * subagent has a run in progress: what a tidy-up's Apply sends, so the
 * file is never rewritten under a run.
 */
export function usePutSubagentMemory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ customerId, template, ...body }: { customerId: string; template: string; text: string; since?: number | null; guard_runs?: boolean }) =>
      apiFetch<SubagentMemory>(`${one(customerId, template)}/memory`, { method: "PUT", body }),
    onSuccess: (view, vars) => qc.setQueryData(blueprintKeys.memory(vars.customerId, vars.template), view),
    onError: (_e, vars) => refreshSubagentMemory(qc, vars.customerId, vars.template),
  });
}

/**
 * One note to the other group (Your coaching / What it learned): the
 * server rewrites that line of the file and nothing else. `since` is the
 * file time the operator was looking at; a file that changed meanwhile is
 * refused (409 `changed_meanwhile`) and reloaded here.
 */
export function useMoveSubagentMemoryNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ customerId, template, ...body }: { customerId: string; template: string } & MoveSubagentMemoryNoteBody) =>
      apiFetch<SubagentMemory>(`${one(customerId, template)}/memory/move`, { method: "POST", body }),
    onSuccess: (view, vars) => qc.setQueryData(blueprintKeys.memory(vars.customerId, vars.template), view),
    onError: (_e, vars) => refreshSubagentMemory(qc, vars.customerId, vars.template),
  });
}

/** How often the tab asks after a tidy-up the server is still running. */
export const CURATE_POLL_MS = 2000;

/** The proposal, as against "still running". */
export function curateReady(reply: SubagentMemoryCurateReply): reply is SubagentMemoryCurate {
  return "proposed" in reply;
}

/**
 * "Tidy up": one inference call proposes a tidied file (duplicates out,
 * near-duplicates merged, contradicted notes under Superseded, every
 * coaching note kept as written) and comes back with a line diff. Nothing
 * is written: the operator applies `proposed` with `usePutSubagentMemory`.
 * The server runs the call as a job and answers 202 while a slow model is
 * still at it (the edge cuts a request at 60 s); this polls GET until the
 * proposal, or the refusal, comes back, so the mutation stays pending for
 * the whole wait. 409 `busy` while the subagent has a run in progress.
 */
export function useCurateSubagentMemory() {
  return useMutation({
    mutationFn: async ({ customerId, template }: { customerId: string; template: string }) => {
      const path = `${one(customerId, template)}/memory/curate`;
      let reply = await apiFetch<SubagentMemoryCurateReply>(path, { method: "POST" });
      while (!curateReady(reply)) {
        await new Promise((resolve) => setTimeout(resolve, CURATE_POLL_MS));
        reply = await apiFetch<SubagentMemoryCurateReply>(path);
      }
      return reply;
    },
  });
}

export function usePutSubagentCaps() {
  const qc = useQueryClient();
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: ({ customerId, caps }: { customerId: string; caps: SubagentCaps }) =>
      apiFetch<SubagentCaps>(`${base(customerId)}/caps`, { method: "PUT", body: caps }),
    onSuccess: (_d, vars) => void qc.invalidateQueries({ queryKey: ["subagent-blueprints", vars.customerId] }),
  });
}

/**
 * The sentence to show for a failed call: the service's own message for a
 * refusal (`{detail: {code, message}}`), the first field error for a
 * request the schema rejected (422 `{detail: [{loc, msg}]}`), a plain
 * "the server hit an error" for a crash, else the transport message.
 */
/** The service's refusal code (`{detail: {code}}`), or null for anything else. */
export function blueprintErrorCode(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  const detail = (error.body as { detail?: unknown } | null)?.detail;
  if (detail && typeof detail === "object" && !Array.isArray(detail)) {
    const code = (detail as { code?: unknown }).code;
    if (typeof code === "string" && code) return code;
  }
  return null;
}

export function blueprintError(error: unknown): string {
  if (error instanceof ApiError) {
    // A crash answers plain text (or a proxy's HTML page), which apiFetch
    // wraps as "non_json_response": say what happened instead. A save writes
    // the manifest before it renders, so part of it may have landed.
    if ((error.body as { error?: unknown } | null)?.error === "non_json_response") {
      return `The server hit an error (${error.status}). Reload to see what was saved, then try again.`;
    }
    const detail = (error.body as { detail?: unknown } | null)?.detail;
    if (detail && typeof detail === "object" && !Array.isArray(detail)) {
      const message = (detail as { message?: unknown }).message;
      if (typeof message === "string" && message) return message;
    }
    if (Array.isArray(detail) && detail.length > 0) {
      const first = detail[0] as { loc?: unknown[]; msg?: unknown };
      const field = Array.isArray(first.loc) ? first.loc.filter((p) => typeof p === "string" && p !== "body").join(" › ") : "";
      if (typeof first.msg === "string") return field ? `${field}: ${first.msg}` : first.msg;
    }
    return error.message;
  }
  return error instanceof Error ? error.message : String(error);
}
